import { useEffect } from 'react'
import { FoundationsButton } from '../../GenericComponents/button'
import { CopyIcon } from '../../GenericComponents/CopyIcon'
import { ApiDTO_NTSPform, NTSP_StatPortalProps } from './Ntsp_Slice'
import React, { useState } from 'react'
import { convertEasternToLocal, DEFAULT_LKW_TIME, Neurohospitalistkey, teleEEGServiceKey, validateForm } from './NTSP_StatPortal'
import moment from 'moment'
import 'moment-timezone'

const Summary: React.FC<NTSP_StatPortalProps> = ({
    form,
    NTSPElements,
    handleClear,
    handleSave,
    isSaved,
    nurseNotes,
    validationSchema,
    errors,
    setErrors,
}) => {
    if (errors === undefined) errors = {}
    if (setErrors === undefined) setErrors = (errors: any) => {}
    if (validationSchema === undefined) validationSchema = {}
    const [submitted, setSubmitted] = useState<boolean>(false)
    const [formError, setFormError] = useState<string>('')
    const wrapHandleClear = async () => {
        setSubmitted(true)
        const tempErrors = validateForm(form, validationSchema)
        const allErrors = { ...errors, ...tempErrors }
        setErrors(allErrors)
        if (Object.values(allErrors).some(Boolean)) {
            setFormError('Please fix the errors before saving.')
            return
        }
        setFormError('')
        if (handleSave) await handleSave(true)
        if (handleClear) handleClear()
    }

    // Helper to format Wake Up Stroke in facility timezone as "M/D/YYYY @HHmm"
    const getWakeUpStrokeDisplay = () => {
        // FP-14084: display Wake Up Stroke Date and Time only if available
        if(form.ts_WakeUpStrokeDate || form.ts_WakeUpStrokeTime)
            return `${form.ts_WakeUpStrokeDate ? form.ts_WakeUpStrokeDate : ''}`
                    +`${form.ts_WakeUpStrokeDate ? ' ' : ''}`
                    +`${form.ts_WakeUpStrokeTime ? '@' + form.ts_WakeUpStrokeTime : ''}`;
        else
            return '';
    }

    // Use the original copyText value for handleCopy
    const copyText = generateFormattedCopyText(form, NTSPElements)

    // Helper function to generate well-formatted copy text
    //FP-14389: Builds CPRS-ready summary text with standardized spacing and conditional optional fields (Consultant/Handoff/Wake-up stroke)
  function generateFormattedCopyText(form: ApiDTO_NTSPform, elements: any) {
    const consultService =
        elements?.ConsultServices?.find((c: any) => c._key === form.fk_ConsultService)?.FriendlyName || 'NA'

    const callerPosition =
        form.fk_CallerPosition > 0
            ? elements?.CallerPosition?.find((c: any) => c._key === form.fk_CallerPosition)?.FriendlyName || ''
            : ''

    const facility =
        elements?.Facilities?.find((c: any) => c._key === form.fk_Facility)?.FacilityName || 'N/A'

    const location =
        elements?.PatientLocations?.find((c: any) => c._key === form.fk_PatientLocation)?.FriendlyName || 'N/A'

    const disposition =
        elements?.HandOffCriteria?.find((e: any) => e._key === form.fk_HandOffCriteria)?.FriendlyName || 'N/A'

    const consultant =
        elements?.Consultants?.find((c: any) => c._key === form.fk_Consultant)?.FriendlyName || ''

    const eegType =
        form.fk_EEGType !== -1
            ? elements?.EEGType?.find((c: any) => c._key === form.fk_EEGType)?.FriendlyName || ''
            : ''

    const consultDate = form.ts_Consult ? moment(form.ts_Consult).format('MM/DD/YYYY HH:mm') : 'N/A'
    const lkwMoment = form.ts_LKW ? moment(form.ts_LKW) : null
    const lkwDate = form.ts_LKWDate && moment(form.ts_LKWDate).isValid()
        ? moment(form.ts_LKWDate).format('MM/DD/YYYY')
        : lkwMoment && lkwMoment.isValid()
            ? lkwMoment.format('MM/DD/YYYY')
            : ''
    const lkwTime = form.ts_LKWTime && form.ts_LKWTime !== DEFAULT_LKW_TIME
        ? form.ts_LKWTime
        : lkwMoment && lkwMoment.isValid()
            ? lkwMoment.format('HH:mm')
            : ''
    const handoffTime = form.ts_HandOff
        ? new Date(form.ts_HandOff).toLocaleString('en-us', { hour12: false })
        : ''

    const symptomLines = (Array.isArray(form.Symptoms) ? form.Symptoms : [])
        .map((id) => elements?.Symptoms?.find((s: any) => s._key === id)?.FriendlyName || '')
        .filter((s) => !!s)

    const noteLines = Array.isArray(nurseNotes)
        ? nurseNotes
              .flatMap((note: any) => {
                  if (typeof note === 'string') {
                      return note.replace(/<br\s*\/?>/gi, '\n')
                  }
                  const name = note?.PosterByUserFullName ? note.PosterByUserFullName + ': ' : ''
                  const text = note?.Note ? String(note.Note).replace(/<br\s*\/?>/gi, '\n') : ''
                  return (name + text).trim()
              })
              .filter((n: string) => !!n)
        : []

    const strokeLabel =
        form.fk_ConsultService !== teleEEGServiceKey && form.fk_ConsultService !== Neurohospitalistkey
            ? ' during the code stroke'
            : ''

    const sectionHeader = form.fk_ConsultService !== teleEEGServiceKey ? 'INITIAL SYMPTOMS REPORTED' : 'Clinical Indication'

    const lines: string[] = []

    lines.push(consultService + (form.fk_ConsultService !== teleEEGServiceKey ? ' Stroke' : '') + ' Nurse Triage')
    lines.push('')

    lines.push('IDENTIFYING INFORMATION:')
    lines.push('Patient facility date/time of the call: ' + consultDate)
    lines.push('Name of Caller: ' + (form.CallerFullName || 'N/A') + (callerPosition ? ', ' + callerPosition : ''))
    lines.push('Provider caring for the patient' + strokeLabel + ': ' + (form.ProviderCaringName || 'N/A'))
    lines.push('')

    lines.push('VA Facility: ' + facility)
    lines.push('Location in VA facility: ' + location)
    lines.push('')

    lines.push(sectionHeader + ':')
    if (symptomLines.length > 0) {
        lines.push(...symptomLines)
    } else {
        lines.push('N/A')
    }
    lines.push('')

    if (form.fk_ConsultService !== teleEEGServiceKey && form.fk_ConsultService !== Neurohospitalistkey) {
        let hasClinicalTimingContent = false

        if (form.flag_UnknownLKW) {
            lines.push('LKW Unknown')
            hasClinicalTimingContent = true
        } else {
            if (lkwDate) {
                lines.push('Last known well date: ' + lkwDate)
                hasClinicalTimingContent = true
            }
            if (lkwTime) {
                lines.push('Last known well time: ' + lkwTime)
                hasClinicalTimingContent = true
            }
        }

        if (form.ts_WakeUpStrokeDate || form.ts_WakeUpStrokeTime) {
            lines.push('Wake up stroke: ' + getWakeUpStrokeDisplay())
            hasClinicalTimingContent = true
        }

        if (hasClinicalTimingContent) {
            lines.push('')
        }
    }

    lines.push('DISPOSITION: ' + disposition)

    if (form.flag_HandOff2NTSPConsult) {
        lines.push('Site Requesting Telephone Consultation With National Telestroke Program (NTSP) Neurologist')
    }

    const hasConsultant = !!form.fk_Consultant && form.fk_Consultant !== -1 && !!consultant
    if (hasConsultant) {
        lines.push('Consultant: ' + consultant)
    }

    if (form.ts_HandOff) {
        lines.push('Handoff Date/Time: ' + handoffTime)
    }

    if (eegType && form.fk_ConsultService === teleEEGServiceKey) {
        lines.push('EEG Type: ' + eegType)
    }

    lines.push('')
    lines.push('NOTES:')
    if (noteLines.length > 0) {
        lines.push(...noteLines)
    }

    return lines.join('\n')
}

return (
        <div style={{ padding: 32, borderRadius: 8 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                <h2 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>
                    {NTSPElements?.ConsultServices?.find((c) => c._key === form.fk_ConsultService)
                        ?.FriendlyName || '________'}{' '}
                        {form.fk_ConsultService!=teleEEGServiceKey ? 'Stroke':''} Nurse Triage Note
                </h2>
                {/* Use CopyIcon component */}
                <CopyIcon alwaysShow={true} copyText={copyText} id={'ntsp_summary'} />
            </div>
            <div style={{ display: 'flex', marginTop: 24, gap: 80 }}>
                {/* Left Column */}
                <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, marginBottom: '1rem' }}>
                        IDENTIFYING INFORMATION:
                    </div>
                    <div style={{ marginBottom: '1rem' }}>
                        <div>
                            <span>Patient facility date/time of the call:</span>{' '}
                            <span>
                                {form.ts_Consult
                                    ? moment(form.ts_Consult)?.format('MM/DD/YYYY HH:mm')
                                    : 'N/A'}
                            </span>
                        </div>
                    </div>
                    <div style={{ marginBottom: '1rem' }}>
                        <span>Name of Caller:</span> {form.CallerFullName || 'N/A'}
                        {','}{' '}
                        {form.fk_CallerPosition > 0
                            ? NTSPElements?.CallerPosition?.find(
                                  (c) => c._key === form.fk_CallerPosition
                              )?.FriendlyName
                            : ''}
                    </div>
                    {/* <div style={{ marginBottom: '1rem' }}>
            <span>Direct Telephone Number of Caller:</span>{" "}
            {form.CallerPhoneNumber}
          </div> */}
                    <div style={{ marginBottom: '1rem' }}>
                        <span>Provider caring for the patient{form.fk_ConsultService!=teleEEGServiceKey && form.fk_ConsultService!=Neurohospitalistkey?' during the code stroke':''}:</span>{' '}
                        <span>{form.ProviderCaringName || 'N/A'}</span>
                    </div>
                    <div style={{ marginBottom: '1rem' }}>
                        <span>VA Facility:</span>{' '}
                        <span style={{ marginBottom: '1rem' }}>
                            {NTSPElements?.Facilities?.find((c) => c._key === form.fk_Facility)
                                ?.FacilityName || 'N/A'}
                        </span>
                    </div>
                    <div style={{ marginBottom: '1rem' }}>
                        <span>Location in VA facility:</span>{' '}
                        {NTSPElements?.PatientLocations?.find(
                            (c) => c._key === form.fk_PatientLocation
                        )?.FriendlyName || 'N/A'}
                    </div>
                </div>
                {/* Right Column */}
                <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, marginBottom: '1rem' }}>{form.fk_ConsultService!=teleEEGServiceKey?'INITIAL SYMPTOMS REPORTED':'Clinical Indication'}:</div>
                    {(() => {
                        // Group symptoms by SymptomType
                        const symptomsByType: { [key: string]: string[] } = {}
                        
                        form.Symptoms?.forEach((symptom) => {
                            const symptomData = NTSPElements?.Symptoms?.find((s: any) => s._key === symptom)
                            if (symptomData) {
                                const type = symptomData.SymptomType || 'Other'
                                if (!symptomsByType[type]) {
                                    symptomsByType[type] = []
                                }
                                symptomsByType[type].push(symptomData.FriendlyName)
                            }
                        })
                        
                        // Render grouped symptoms
                        return Object.entries(symptomsByType).map(([type, symptoms]) => (
                            <div className="mb-1" key={type}>
                                {type}: {symptoms.join(', ')}
                            </div>
                        ))
                    })()}
                    {/* <div style={{ marginBottom: 4 }}>BEFAST Symptoms:</div> */}
                    {/* <div style={{ marginLeft: 24, marginBottom: 4 }}>
            {Array.isArray(form.BefastSymptoms) && form.BefastSymptoms.length > 0
              ? form.BefastSymptoms
                  .map(
                    (symptomKey: number) =>
                      NTSPElements?.BefastSymptoms?.find(c => c._key === symptomKey)?.FriendlyName || symptomKey
                  )
                  .join(", ")
              : "N/A"}
          </div> */}
                    {/* <div style={{ marginBottom: 4 }}>Large Vessel Symptoms:</div> */}
                    {/* <div style={{ marginLeft: 24, marginBottom: 4 }}>
            {Array.isArray(form.LargeVesselSymptoms) && form.LargeVesselSymptoms.length > 0
              ? form.LargeVesselSymptoms
                  .map(
                    (symptomKey: number) =>
                      NTSPElements?.LargeVesselSymptoms?.find(c => c._key === symptomKey)?.FriendlyName || symptomKey
                  )   
                  .join(", ")
              : "N/A"}
          </div> */}
                    {form.fk_ConsultService!=teleEEGServiceKey && form.fk_ConsultService!=Neurohospitalistkey &&
                        <>
                        {
                            // FP-14084: if LKW Unknown Checkbox not selected and valid LKW date
                            form.ts_LKWDate && moment(form.ts_LKWDate).isValid() && !form.flag_UnknownLKW &&
                            <div style={{ marginBottom: '1rem', marginTop: '1rem' }}>
                                <span>Last known well date: </span>
                                {moment(form.ts_LKWDate)?.format('MM/DD/YYYY')} {/* FP-14084: format date */}
                            </div>
                        }
                        {
                            // FP-14084: if LKW Unknown Checkbox not selected and valid LKW time
                            form.ts_LKWTime && form.ts_LKWTime !== DEFAULT_LKW_TIME && !form.flag_UnknownLKW &&
                            <div style={{ marginBottom: '1rem' }}>
                                <span>Last known well time: </span>
                                {form.ts_LKWTime}
                            </div>
                        }
                        {/* FP-14084: if LKW Unknown Checkbox selected */}
                        {
                            form.flag_UnknownLKW &&
                            <div style={{ marginBottom: '1rem' }}>
                                <span>LKW Unknown</span>
                            </div>
                        }   {/* FP-14084: close block */}
                        </>
                    }
                    {form.fk_ConsultService!=teleEEGServiceKey && form.fk_ConsultService!=Neurohospitalistkey &&
                    (form.ts_WakeUpStrokeDate || form.ts_WakeUpStrokeTime) && (
                        <div style={{ marginBottom: '1rem' }}>
                            <span>Wake up stroke: </span>
                            {getWakeUpStrokeDisplay()}
                        </div>
                    )
                    }
                    
                     
                    <div style={{ marginBottom: '1rem' }}>
                        <span style={{ fontWeight: 600 }}>DISPOSITION:</span>{' '}
                        {NTSPElements?.HandOffCriteria?.find(
                            (element) => element._key === form.fk_HandOffCriteria
                        )?.FriendlyName || ''}
                        <br/>
                        {form.flag_HandOff2NTSPConsult ? 'Site Requesting Telephone Consultation With National Telestroke Program (NTSP) Neurologist' : ''}
                    </div>
                    {/* ${form.fk_EEGType ? `EEG Type: ${NTSPElements?.EEGType?.find(c=>c._key===form.fk_EEGType)?.FriendlyName || "N/A"}` : ""} */}
                        {form.fk_EEGType !== -1 && form.fk_ConsultService === teleEEGServiceKey && (
                        <div style={{ marginBottom: '1rem' }}>
                            <span>
                                {' '}
                                EEG Type:{' '}
                                {NTSPElements?.EEGType?.find((c) => c._key === form.fk_EEGType)
                                    ?.FriendlyName || 'N/A'}{' '}
                            </span>
                        </div>
                    )}

                    <div style={{ marginBottom: '1rem' }}>
                        <span>
                            {' '}
                            Consultant:{' '}
                            {NTSPElements?.Consultants?.find((c) => c._key === form.fk_Consultant)
                                ?.FriendlyName || 'N/A'}{' '}
                        </span>
                    </div>
                    <div>
                        <span>
                            {' '}
                            Handoff Date/Time:{' '}
                            {form.ts_HandOff
                                ? new Date(form.ts_HandOff).toLocaleString('en-us', {
                                      hour12: false,
                                  })
                                : 'N/A'}{' '}
                        </span>
                    </div>
                    <div>
                        <span>NOTES:&nbsp;</span>
                        <span
                            style={{
                                display: 'block',
                                maxHeight: 120,
                                overflowY: 'auto',
                                whiteSpace: 'pre-wrap',
                                verticalAlign: 'top',
                                width: '100%',
                                wordBreak: 'break-all'
                            }}
                            dangerouslySetInnerHTML={{
                                __html:
                                    Array.isArray(nurseNotes) && nurseNotes.length > 0
                                        ? nurseNotes
                                            .map((note: any) => {
                                                if (typeof note === 'string') {
                                                    return note.replace(/<br>/g, ', ')
                                                }
                                                const name = note?.PostedByUserFullName ? `${note.PostedByUserFullName}: ` : ''
                                                const text = note?.Note ? note.Note.replace(/<br>/g, ', ') : ''
                                                return `${name}${text}`
                                            })
                                            .join('<br />')
                                        : ''
                            }}
                        />
                    </div>
                </div>
            </div>
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.5rem',
                    minWidth: '250px',
                    justifyContent: 'flex-end',
                    flex: 1,
                    alignItems: 'end',
                }}
            >
                <div style={{ display: 'flex', gap: '1rem' }}>
                    <FoundationsButton
                        buttonClasses="app-button"
                        onClick={wrapHandleClear}
                        id={''}
                        toolTipText="Currently opened case entry form will be saved and closed."
                        tooltipClasses="info-icon"
                    >
                        Close
                    </FoundationsButton>
                </div>
                {submitted && formError !== '' && (
                    <div style={{ color: 'red', textAlign: 'right', marginTop: '0.5rem' }}>
                        {formError}
                    </div>
                )}
            </div>
        </div>
    )
}

export default Summary
